import { useMutation, useQueryClient } from '@tanstack/react-query';

import { providerService } from '@/services/provider-service';
import { pollForResourceGone } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { providerMutationKeys, providerQueryKeys } from './query-keys';

/**
 * Hook to delete a provider feature.
 * Returns a react-query mutation that accepts a feature ID.
 * Invalidates the provider features cache to trigger a refetch.
 */
export function useDeleteProviderFeature(tenantId?: UUID, providerId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: providerMutationKeys.deleteProviderFeature(
      tenantId,
      providerId
    ),
    mutationFn: async (featureId: UUID) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!providerId) throw new Error('Missing provider id');

      const removed = await providerService.removeProviderFeature(
        tenantId,
        providerId,
        featureId
      );

      if (!removed) {
        throw new Error('Failed to delete provider feature');
      }

      await pollForResourceGone(async () => {
        const page = await providerService.listProviderFeatures(
          tenantId,
          providerId
        );
        return page.items.find((feature) => feature.id === featureId) ?? null;
      });

      return featureId;
    },
    onSuccess: (_, featureId) => {
      // Provider feature mutations can create/update connector feature projections,
      // so invalidate all feature-query families for this tenant.
      qc.invalidateQueries({
        queryKey: providerQueryKeys.featureListByTenant(tenantId),
        refetchType: 'all',
      });
      qc.invalidateQueries({
        queryKey:
          providerQueryKeys.connectorFeaturesWithProviderOptionsByTenant(
            tenantId
          ),
        refetchType: 'all',
      });

      // Keep existing entity-level invalidations for detail views.
      qc.invalidateQueries({
        queryKey: providerQueryKeys.featureListByProvider(tenantId, providerId),
        refetchType: 'all',
      });
      qc.removeQueries({
        queryKey: providerQueryKeys.featureDetail(
          tenantId,
          providerId,
          featureId
        ),
      });
      qc.invalidateQueries({
        queryKey: providerQueryKeys.detail(tenantId, providerId),
        refetchType: 'all',
      });
    },
  });
}
